/**
 * Original/Japanese-translation display toggle for a release page
 * (spec-translation.md 表示: トグル UI).
 *
 * internal/site/templates/releases.templ's ReleaseContent renders the
 * entire release page content inside a single
 * `.release-lang-root[data-active-lang="en"]` element (the page's `<article
 * class="release-detail">`), and, only when at least one translation exists
 * on the page (ConvertToReleaseDetailData's HasTranslations), a
 * `.release-lang-toggle` button rendered `hidden` inside it - so a no-JS
 * visitor never sees a control that would do nothing (progressive
 * enhancement, the same posture as release-toc.ts's filter chips: this
 * module's only job on load is to reveal that button and wire its click
 * handler).
 *
 * Every translated Section heading / Item body is rendered twice by
 * release_markdown.go, as `[data-lang="en"]` / `[data-lang="ja"]` sibling
 * elements carrying the literal `release-lang-en` / `release-lang-ja-block`
 * (item bodies) or `release-lang-ja-inline` (heading spans) UnoCSS shortcuts
 * (uno.config.mjs) - plain CSS attribute-selector rules keyed off the root's
 * own `data-active-lang` attribute. This module therefore never touches any
 * `[data-lang]` element directly; it only ever flips that one root
 * attribute (and the button's own label). An Item/heading with no
 * translation is rendered as a single element with no `data-lang` attribute
 * at all, so it is never hidden in either mode regardless of
 * `data-active-lang` (spec-translation.md: "翻訳が無い Item は ja モードでも
 * 原文を表示").
 *
 * release-item-lang.ts lets a reader additionally pin one Item away from
 * this page default via its own `data-lang-override` attribute (task #16).
 * This page-wide toggle always wins over those: toggleReleaseLang calls
 * that module's resyncItemLangToggles right after flipping the root's
 * `data-active-lang`, clearing every Item's override so the whole page
 * switches together on this control, exactly as it did before per-Item
 * overrides existed.
 */

import { resyncItemLangToggles } from './release-item-lang.js';

const ROOT_SELECTOR = '.release-lang-root';
const TOGGLE_SELECTOR = '.release-lang-toggle';
const LABEL_SELECTOR = '.release-lang-toggle-label';

const LANG_EN = 'en';
const LANG_JA = 'ja';

// The label always names the language the *next* click would switch to
// (spec-translation.md: "切替後は「原文で表示」に変わる" - i.e. once
// displaying Japanese, the button offers to go back to the original).
const LABEL_TO_JA = '日本語で表示';
const LABEL_TO_EN = '原文で表示';

/** Marks a `.release-lang-toggle` whose click listener is already bound. */
const INITIALIZED_ATTR = 'data-lang-toggle-initialized';

/**
 * Toggles root's `data-active-lang` between "en" and "ja" (defaulting the
 * "current" reading to "en" for any unexpected/missing attribute value, so
 * a first click always moves to "ja") and updates toggle's visible label to
 * name the language a further click would switch to. Exported for testing.
 */
export function toggleReleaseLang(root: HTMLElement, toggle: HTMLElement): void {
  const current = root.dataset.activeLang === LANG_JA ? LANG_JA : LANG_EN;
  const next = current === LANG_JA ? LANG_EN : LANG_JA;
  root.dataset.activeLang = next;

  resyncItemLangToggles(root);

  const label = toggle.querySelector<HTMLElement>(LABEL_SELECTOR);
  const text = next === LANG_JA ? LABEL_TO_EN : LABEL_TO_JA;
  if (label) {
    label.textContent = text;
  } else {
    toggle.textContent = text;
  }
  toggle.setAttribute('aria-pressed', String(next === LANG_JA));
}

/**
 * Reveals every page's `.release-lang-toggle` (server-rendered `hidden`,
 * per the progressive-enhancement posture above) and wires its click
 * handler to flip its enclosing `.release-lang-root`'s `data-active-lang`.
 * Safe to call multiple times: a toggle already marked with
 * INITIALIZED_ATTR only has its `hidden` re-checked (a no-op, since it was
 * already cleared), never a second click listener bound. Exported for
 * testing.
 */
export function initializeReleaseLangToggle(): void {
  document.querySelectorAll<HTMLElement>(ROOT_SELECTOR).forEach((root) => {
    const toggle = root.querySelector<HTMLElement>(TOGGLE_SELECTOR);
    if (!toggle) return; // No translations on this release: nothing to toggle.

    toggle.hidden = false;
    if (toggle.hasAttribute(INITIALIZED_ATTR)) return;
    toggle.setAttribute(INITIALIZED_ATTR, 'true');
    toggle.setAttribute('aria-pressed', 'false');

    toggle.addEventListener('click', () => toggleReleaseLang(root, toggle));
  });
}
